import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';

const botSubmissionSchema = z.object({
  name: z.string().trim().min(1, 'Bot name is required').max(100, 'Name must be under 100 characters'),
  description: z.string().trim().min(10, 'Description must be at least 10 characters').max(2000, 'Description must be under 2000 characters'),
  category: z.string().min(1, 'Category is required'),
  pricing: z.enum(['Free', 'Freemium', 'Paid'], { required_error: 'Pricing model is required' }),
  website_url: z.string().trim().url('Must be a valid URL').optional().or(z.literal('')),
  logo_url: z.string().trim().url('Must be a valid URL').optional().or(z.literal('')),
  model_type: z.string().max(50).optional().or(z.literal('')),
  login_required: z.boolean(),
  features: z.array(z.string().max(50)).max(20, 'Maximum 20 features allowed'),
  submission_notes: z.string().max(1000, 'Notes must be under 1000 characters').optional().or(z.literal(''))
});

export default function BotSubmissionForm() {
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    pricing: '',
    website_url: '',
    logo_url: '',
    model_type: '',
    login_required: false,
    features: [] as string[],
    submission_notes: ''
  });

  const categories = [
    'Chatbot', 'Writing Assistant', 'Code Assistant', 'Image Generation', 
    'Data Analysis', 'Customer Service', 'Education', 'Entertainment', 
    'Productivity', 'Health & Fitness', 'Finance', 'Other'
  ];

  const pricingOptions = ['Free', 'Freemium', 'Paid'];
  const modelTypes = ['GPT-4', 'GPT-3.5', 'Claude', 'Gemini', 'Llama', 'Custom', 'Other'];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isAuthenticated || !user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to submit a bot",
        variant: "destructive"
      });
      navigate('/auth');
      return;
    }

    // Validate form data
    const validation = botSubmissionSchema.safeParse(formData);
    if (!validation.success) {
      const firstError = validation.error.errors[0];
      toast({
        title: "Validation error",
        description: firstError.message,
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const validData = validation.data;
      const { error } = await supabase
        .from('bot_submissions')
        .insert({
          user_id: user.id,
          name: validData.name,
          description: validData.description,
          category: validData.category,
          pricing: validData.pricing,
          website_url: validData.website_url || null,
          logo_url: validData.logo_url || null,
          model_type: validData.model_type || null,
          login_required: validData.login_required,
          features: validData.features.length > 0 ? validData.features : null,
          submission_notes: validData.submission_notes || null
        });

      if (error) throw error;

      toast({
        title: "Submission successful!",
        description: "Your bot has been submitted for review. We'll notify you once it's approved."
      });

      navigate('/');
    } catch (error: any) {
      toast({
        title: "Submission failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureChange = (feature: string, checked: boolean) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, feature]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        features: prev.features.filter(f => f !== feature)
      }));
    }
  };

  const commonFeatures = [
    'API Access', 'Custom Training', 'Multi-language', 'Voice Input', 
    'File Upload', 'Team Collaboration', 'Analytics', 'Integrations'
  ];

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Authentication Required</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">Please sign in to submit a bot for review.</p>
            <Button onClick={() => navigate('/auth')} className="w-full">
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-accent/5 p-4">
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-bold">Submit Your AI Bot</CardTitle>
            <p className="text-muted-foreground">
              Help others discover your AI bot by submitting it to our directory
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Bot Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter bot name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(category => (
                        <SelectItem key={category} value={category}>{category}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe what your bot does and its key features"
                  rows={4}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pricing">Pricing *</Label>
                  <Select value={formData.pricing} onValueChange={(value) => setFormData(prev => ({ ...prev, pricing: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select pricing model" />
                    </SelectTrigger>
                    <SelectContent>
                      {pricingOptions.map(option => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="model_type">Model Type</Label>
                  <Select value={formData.model_type} onValueChange={(value) => setFormData(prev => ({ ...prev, model_type: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select model type" />
                    </SelectTrigger>
                    <SelectContent>
                      {modelTypes.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="website_url">Website URL</Label>
                  <Input
                    id="website_url"
                    type="url"
                    value={formData.website_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, website_url: e.target.value }))}
                    placeholder="https://your-bot-website.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="logo_url">Logo URL</Label>
                  <Input
                    id="logo_url"
                    type="url"
                    value={formData.logo_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, logo_url: e.target.value }))}
                    placeholder="https://your-logo-url.com/logo.png"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Features</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {commonFeatures.map(feature => (
                    <div key={feature} className="flex items-center space-x-2">
                      <Checkbox
                        id={feature}
                        checked={formData.features.includes(feature)}
                        onCheckedChange={(checked) => handleFeatureChange(feature, checked as boolean)}
                      />
                      <Label htmlFor={feature} className="text-sm">{feature}</Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="login_required"
                  checked={formData.login_required}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, login_required: checked as boolean }))}
                />
                <Label htmlFor="login_required">Requires user login/registration</Label>
              </div>

              <div className="space-y-2">
                <Label htmlFor="submission_notes">Additional Notes</Label>
                <Textarea
                  id="submission_notes"
                  value={formData.submission_notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, submission_notes: e.target.value }))}
                  placeholder="Any additional information you'd like to share about your bot"
                  rows={3}
                />
              </div>

              <Button type="submit" disabled={loading || !formData.name || !formData.description || !formData.category || !formData.pricing} className="w-full">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Submit Bot for Review
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}